/**
 * @file OptionSheet.tsx
 * @description Выбор одного значения в нижней шторке (phase19.31, шаг О4):
 * на телефоне и планшете фильтр вроде «Склад» не помещается в строку
 * фильтров и уходит в «⋯» страницы — пункт меню открывает эту шторку.
 *
 * Пункты — кнопки высотой как у кнопок темы (48, у кладовщика 56),
 * выбранный отмечен галочкой и `aria-pressed`. Выбор закрывает шторку.
 *
 * ```tsx
 * <OptionSheet
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Склад"
 *   value={warehouseId}
 *   options={[{ value: "", label: "Все склады" }, ...warehouses]}
 *   onChange={setWarehouseId}
 * />
 * ```
 */

import { Check } from "lucide-react";

import { Button } from "./button";
import type { SelectFieldOption } from "./SelectField";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./sheet";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  value: string;
  options: SelectFieldOption[];
  onChange: (value: string) => void;
}

export function OptionSheet({ open, onOpenChange, title, value, options, onChange }: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="gap-2 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
        <SheetHeader>
          <SheetTitle className="type-card-title">{title}</SheetTitle>
        </SheetHeader>
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <Button
              key={option.value}
              variant={selected ? "soft" : "ghost"}
              aria-pressed={selected}
              disabled={option.disabled}
              className="w-full justify-between"
              onClick={() => {
                onChange(option.value);
                onOpenChange(false);
              }}
            >
              <span className="truncate">{option.label}</span>
              {selected ? <Check className="size-4" /> : null}
            </Button>
          );
        })}
      </SheetContent>
    </Sheet>
  );
}
